import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Wallet } from 'lucide-react';
import { useGetList } from 'ra-core';
import { Link } from 'react-router-dom';

const BUCKETS = [
    { key: 'bucket_0_30', label: '0-30d', color: 'bg-emerald-500' },
    { key: 'bucket_31_60', label: '31-60d', color: 'bg-amber-500' },
    { key: 'bucket_61_90', label: '61-90d', color: 'bg-orange-500' },
    { key: 'bucket_90_plus', label: '90d+', color: 'bg-red-600' },
] as const;

const formatLakh = (v: number) => `₹${(v / 100000).toFixed(1)}L`;

/**
 * ReceivablesAgingWidget
 *
 * Dashboard card summarizing outstanding customer receivables by age bucket,
 * sourced from the customer_aging_view (built for this purpose but previously unused).
 */
export const ReceivablesAgingWidget = () => {
    const { data: aging, isPending } = useGetList('customer_aging_view', {
        pagination: { page: 1, perPage: 500 },
        filter: {},
    });

    const totals = BUCKETS.map((b) => ({
        ...b,
        value: aging?.reduce((sum, row) => sum + (Number(row[b.key]) || 0), 0) ?? 0,
    }));
    const totalOutstanding = totals.reduce((sum, b) => sum + b.value, 0);
    const breachCount = aging?.filter((row) => row.breach_flag).length ?? 0;

    return (
        <Card className="border-none shadow-sm">
            <CardHeader className="pb-3 border-b">
                <CardTitle className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                    <Wallet className="w-3.5 h-3.5" /> Receivables Aging
                    {breachCount > 0 && (
                        <Badge variant="outline" className="ml-auto text-[9px] bg-destructive/10 text-destructive border-destructive/30 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> {breachCount} over credit limit
                        </Badge>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-3">
                {isPending ? (
                    <div className="py-6 text-center text-muted-foreground text-xs animate-pulse">Loading receivables…</div>
                ) : !aging?.length ? (
                    <div className="py-6 text-center text-muted-foreground flex flex-col items-center gap-2">
                        <Wallet className="w-8 h-8 opacity-40" />
                        <p className="text-xs">No open receivables.</p>
                    </div>
                ) : (
                    <>
                        <div className="text-2xl font-black text-foreground mb-3">{formatLakh(totalOutstanding)}</div>
                        <div className="flex w-full h-2.5 rounded-full overflow-hidden mb-3 bg-muted">
                            {totals.map((b) => (
                                totalOutstanding > 0 && (
                                    <div
                                        key={b.key}
                                        className={b.color}
                                        style={{ width: `${(b.value / totalOutstanding) * 100}%` }}
                                    />
                                )
                            ))}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {totals.map((b) => (
                                <div key={b.key} className="flex items-center gap-1.5 text-xs">
                                    <span className={`w-2 h-2 rounded-full shrink-0 ${b.color}`} />
                                    <span className="text-muted-foreground">{b.label}</span>
                                    <span className="ml-auto font-semibold text-foreground">{formatLakh(b.value)}</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                <div className="pt-3 mt-2 border-t text-center">
                    <Link to="/customer_aging_view" className="text-[11px] text-blue-600 hover:underline font-semibold">
                        View aging detail →
                    </Link>
                </div>
            </CardContent>
        </Card>
    );
};
